import axios, { AxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import { uniqBy } from '../../util/arrayUtils';
import { Feature } from 'ol';
import { GeoJSONLineString, GeoJSONPolygon, GeoJSONMultiPolygon } from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AUTHED_REQUEST_CONFIG } from '../../store/auth';
import { TRIGGERS_URL } from '../../store/url';
import { CID, PID } from '../../store/user';
import {
  microfencesToMicrofenceCluster,
  featuresToLayer,
  LayerResult,
  jsonToPointCluster,
} from '../../Components/Map/LiveAndReplay/TypeConversions';
import { containsExtent, Extent } from 'ol/extent';
import { transform } from 'ol/proj';
import { LAYER_GROUPS } from '../../store/map';
import {
  initialExtentInDegrees,
  initialLatitude,
  initialLongitude,
  POINT,
} from '../../util/constants';
import { FenceZone, GeofenceEntityType } from '../../util/enums';
import { Bounds } from '../../Components/Map/types';
import { Microfence } from '../../API/types/triggers';
import { SimpleGeofence } from '../../util/Events/Messages';

export interface StrongFeatureHolder<G extends Geometry, D> {
  feature: Feature<G>;
  data: D;
}

export interface PointsLayersMicrofences {
  points: { id: string; name: string; source: StrongFeatureHolder<Point, SimpleGeofence>[] }[];
  layers: { id: string; name: string; source: StrongFeatureHolder<Polygon, SimpleGeofence>[] }[];
  microfences: StrongFeatureHolder<Point, Microfence>[];
}

export function useLiveMapLoader() {
  const storeLayerGroups = useSetAtom(LAYER_GROUPS);
  const [layerData, setLayerData] = useState<
    | { id: string; name: string; source: StrongFeatureHolder<Polygon, SimpleGeofence>[] }[]
    | undefined
  >();
  const [microfencesData, setMicrofencesData] = useState<
    StrongFeatureHolder<Point, Microfence>[] | undefined
  >();
  const [pointsData, setPointsData] = useState<
    { id: string; name: string; source: StrongFeatureHolder<Point, SimpleGeofence>[] }[] | undefined
  >();
  const [result, setResult] = useState<PointsLayersMicrofences | undefined>();
  const [bounds, setBounds] = useState<Bounds>({
    latitude: initialLatitude,
    longitude: initialLongitude,
    extentInDegrees: initialExtentInDegrees,
  });
  const [activeRequests, setActiveRequests] = useState<number>(0);
  const abortController = useRef<AbortController | undefined>(undefined);
  const isLoading = useMemo(() => activeRequests > 0, [activeRequests]);
  const knownExtents = useRef<Extent[]>([]);

  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);
  const triggersUrl = useAtomValue(TRIGGERS_URL);
  const authedRequestConfig = useAtomValue(AUTHED_REQUEST_CONFIG);

  useEffect(() => {
    const fetchData = async () => {
      const [layers, microfences, geofencePoints] = await Promise.all([
        axios.get<
          {
            id: string;
            name: string;
            polygons: {
              id: string;
              name: string;
              points: GeoJSONPolygon;
              zone?: FenceZone;
            }[];
            multipolygons: {
              id: string;
              name: string;
              points: GeoJSONMultiPolygon;
              zone?: FenceZone;
            }[];
            lines: { id: string; name: string; points: GeoJSONLineString }[];
          }[]
        >(
          `${triggersUrl}/${cid}/${pid}/geofences/withinExtent?latitude=${bounds.latitude}&longitude=${bounds.longitude}&extentInDegrees=${bounds.extentInDegrees}`,
          authedRequestConfig,
        ),
        // TODO: Should propbably have some kind of 'Simplified' of 'WithinExtent'... Utlra low priority, but kinda defeates the purpose of having pagination in the first place.
        // https://geomoby.atlassian.net/browse/LTP-1484
        axios.get<Microfence[]>(`${triggersUrl}/${cid}/${pid}/microfences`, authedRequestConfig),
        axios.get<
          {
            fenceId: string;
            name: string;
            fenceType: GeofenceEntityType;
            layerId: string;
            point: { type: string; coordinates: string[] };
          }[]
        >(`${triggersUrl}/${cid}/${pid}/geofences/simplified`, authedRequestConfig),
      ]);
      const lrs: LayerResult[] = layers.data.map(({ id, name, polygons, multipolygons, lines }) =>
        featuresToLayer(id, name, [
          ...polygons,
          ...multipolygons.map(m => {
            return {
              ...m,
              zone: FenceZone.cleared,
            };
          }),
          ...lines,
        ]),
      );

      setLayerData(
        lrs.map(({ lid, name, layer }) => {
          return { id: lid, name, source: layer };
        }),
      );

      setMicrofencesData(microfencesToMicrofenceCluster(microfences.data));
      setPointsData(
        lrs.map(({ lid, name }) => {
          return {
            id: lid,
            name,
            source: jsonToPointCluster(
              geofencePoints.data
                .filter(p => p.layerId === lid)
                .map(f => {
                  return {
                    geofence: {
                      id: f.fenceId,
                      name: f.name,
                      fenceType: f.fenceType,
                      type: POINT,
                      layerId: f.layerId,
                    },
                    point: f.point,
                  };
                }),
            ),
          };
        }),
      );
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!layerData) return;

    const loadMoreFeatures = async () => {
      const { latitude, longitude, extentInDegrees } = bounds;
      const [xMax, yMax] = transform(
        [longitude + extentInDegrees, latitude + extentInDegrees / 2],
        'EPSG:4326',
        'EPSG:3857',
      );
      const [xMin, yMin] = transform(
        [longitude - extentInDegrees, latitude - extentInDegrees / 2],
        'EPSG:4326',
        'EPSG:3857',
      );
      const extent: [number, number, number, number] = [xMax, yMax, xMin, yMin];
      // already got features for this extent
      if (knownExtents.current.some(ext => containsExtent(ext, extent))) return;

      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();
      setActiveRequests(n => n + 1);
      const newLayers = await axios
        .get<
          {
            id: string;
            name: string;
            polygons: { id: string; name: string; points: GeoJSONPolygon }[];
            multipolygons: {
              id: string;
              name: string;
              points: GeoJSONMultiPolygon /*points: GeoJSONMultiPolygon*/;
            }[];
            lines: { id: string; name: string; points: GeoJSONLineString }[];
          }[]
        >(
          `${triggersUrl}/${cid}/${pid}/geofences/withinExtent?latitude=${latitude}&longitude=${longitude}&extentInDegrees=${bounds.extentInDegrees}`,
          { ...authedRequestConfig, signal: abortController.current.signal },
        )
        .catch((e: AxiosError) => {
          if (e.message === 'cancelled') {
            console.warn(e);
          } else {
            console.error(e);
          }
          setActiveRequests(n => n - 1);
        });
      if (!newLayers) return;

      knownExtents.current = [...knownExtents.current, extent];

      const newLrs: LayerResult[] = newLayers.data.map(
        ({ id, name, polygons, multipolygons, lines }) =>
          featuresToLayer(id, name, [
            ...polygons,
            ...multipolygons.map(m => {
              return {
                ...m,
                zone: FenceZone.cleared,
              };
            }),
            ...lines,
          ]),
      );

      setLayerData(layerData => {
        if (!layerData) return layerData;
        return layerData.map(lyr => {
          const newLyr = newLrs.find(nl => nl.lid === lyr.id);
          if (!newLyr) {
            return lyr;
          }
          const updatedLayer = uniqBy([...lyr.source, ...newLyr.layer], ({ data }) => data.id);
          return { ...lyr, source: updatedLayer };
        });
      });
      setActiveRequests(n => n - 1);
    };
    loadMoreFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds]);

  useEffect(() => {
    if (layerData && microfencesData && pointsData) {
      setResult({
        points: pointsData,
        layers: layerData,
        microfences: microfencesData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerData, microfencesData, pointsData]);

  useEffect(() => {
    storeLayerGroups(result);
  }, [storeLayerGroups, result]);

  return { result, bounds, setBounds, loading: isLoading };
}
